import React, { useEffect, useMemo, useState } from "react";
import { Card, Form } from "react-bootstrap";
import { MdFolder } from "react-icons/md";
import axios from "axios";

import { headers, formatDisplayDate } from "../global_vars";
import { documentExpiryColor, documentExpiryRowStyle } from "./documentExpiry";
import { DOCUMENT_STATUS_CHOICES, documentStatusLabel } from "./documentStatus";
import RelatedObjectLink from "./RelatedObjectLink";
import {
  projectLinkLabel,
  documentLinkLabel,
} from "./relatedLinkLabels";
import RichTextDisplay from "./RichTextDisplay";
import DocumentDropzone from "./DocumentDropzone";
import AddDocumentModal from "../modals/create/add_document";
import AddDocumentCategoryModal from "../modals/create/add_document_category";
import AutocompleteSelect from "./AutocompleteSelect";

const ALL_DOCUMENT_CATEGORIES =
  "https://ultima.icsgr.com/api/administration/all_document_categories/";

const overviewIconStyle = { color: "#93ab3c", marginRight: "0.5em" };
const labelPillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "999px",
  background: "#f5f5f5",
  color: "#666",
  fontSize: 12,
  fontWeight: 600,
};
const valueTextStyle = { fontWeight: 700 };
const sectionHeaderStyle = {
  marginTop: 24,
  marginBottom: 8,
  padding: "8px 12px",
  background: "#f4f7ea",
  borderLeft: "4px solid #93ab3c",
  fontWeight: 700,
  color: "#2c3e50",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

function categoryIdOf(doc) {
  return doc?.category?.doccate_id || doc?.category_id || "";
}

function categoryTitleOf(doc) {
  return doc?.category?.title || null;
}

function categoryOrderOf(doc, categoryMetaById) {
  const id = categoryIdOf(doc);
  if (!id) return Number.MAX_SAFE_INTEGER;
  const meta = categoryMetaById[id];
  if (meta && typeof meta.orderindex === "number") return meta.orderindex;
  return 999999;
}

/**
 * Documents tab with search, category/status filters, and grouping by category.
 */
export default function DocumentsTabPanel({
  documents = [],
  refreshData,
  projectId,
  clientId,
  lockProject = false,
  lockClient = false,
  projectAssociatedClients,
}) {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [uploadCategoryId, setUploadCategoryId] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const currentHeaders = {
          ...headers,
          Authorization: "Token " + localStorage.getItem("userToken"),
        };
        const res = await axios.get(ALL_DOCUMENT_CATEGORIES, {
          headers: currentHeaders,
        });
        const list = res?.data?.all_document_categories || [];
        setCategories(Array.isArray(list) ? list.filter((c) => c.active !== false) : []);
      } catch (_e) {
        setCategories([]);
      }
    };
    load();
  }, []);

  const categoryMetaById = useMemo(() => {
    const map = {};
    (categories || []).forEach((c) => {
      if (c?.doccate_id) map[c.doccate_id] = c;
    });
    return map;
  }, [categories]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (Array.isArray(documents) ? documents : []).filter((doc) => {
      if (categoryFilter === "__uncategorized__") {
        if (categoryIdOf(doc)) return false;
      } else if (categoryFilter) {
        if (categoryIdOf(doc) !== categoryFilter) return false;
      }
      if (statusFilter && (doc.status || "") !== statusFilter) return false;
      if (q) {
        const hay = [
          doc.title,
          doc.document_id,
          categoryTitleOf(doc),
          documentStatusLabel(doc.status),
          doc.notes,
          doc.project?.title,
          doc.project?.filecode,
          doc.client?.surname,
          doc.client?.name,
          doc.client?.fullname,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [documents, search, categoryFilter, statusFilter]);

  const grouped = useMemo(() => {
    const groups = new Map();
    filtered.forEach((doc) => {
      const id = categoryIdOf(doc) || "__uncategorized__";
      const title = categoryTitleOf(doc) || "Uncategorized";
      if (!groups.has(id)) {
        groups.set(id, {
          id,
          title,
          order: categoryOrderOf(doc, categoryMetaById),
          docs: [],
        });
      }
      groups.get(id).docs.push(doc);
    });
    return Array.from(groups.values()).sort((a, b) => {
      if (a.id === "__uncategorized__") return 1;
      if (b.id === "__uncategorized__") return -1;
      if (a.order !== b.order) return a.order - b.order;
      return String(a.title).localeCompare(String(b.title));
    });
  }, [filtered, categoryMetaById]);

  const renderDocCard = (doc) => (
    <Card
      key={doc.document_id}
      style={{ marginTop: 16, ...documentExpiryRowStyle(doc.validuntil) }}
    >
      <Card.Header style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h5 style={{ margin: 0 }}>
          <MdFolder style={overviewIconStyle} /> {doc.title || "Document"}
        </h5>
        <RelatedObjectLink
          href={`/data_management/document/${doc.document_id}`}
          label={documentLinkLabel(doc)}
          title={doc.document_id}
        />
      </Card.Header>
      <Card.Body>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {categoryTitleOf(doc) ? (
            <>
              <span style={labelPillStyle}>Category</span>
              <span style={valueTextStyle}>{categoryTitleOf(doc)}</span>
            </>
          ) : (
            <>
              <span style={labelPillStyle}>Category</span>
              <span style={valueTextStyle}>Uncategorized</span>
            </>
          )}
          {doc.created ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Created</span>
              <span style={valueTextStyle}>{formatDisplayDate(doc.created)}</span>
            </>
          ) : null}
          {doc.project?.project_id ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Project</span>
              <RelatedObjectLink
                href={`/data_management/project/${doc.project.project_id}`}
                label={projectLinkLabel(doc.project)}
                title={doc.project.project_id}
              />
            </>
          ) : null}
          {Array.isArray(doc.related_clients) && doc.related_clients.length > 0 ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Clients</span>
              <span style={valueTextStyle}>
                {doc.related_clients
                  .map(
                    (c) =>
                      c.fullname ||
                      `${c.surname || ""} ${c.name || ""}`.trim() ||
                      c.client_id
                  )
                  .join(", ")}
              </span>
            </>
          ) : doc.client ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Client</span>
              <span style={valueTextStyle}>
                {doc.client.fullname ||
                  `${doc.client.surname || ""} ${doc.client.name || ""}`.trim() ||
                  doc.client.client_id}
              </span>
            </>
          ) : null}
          {typeof doc.original === "boolean" ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Original</span>
              <span style={valueTextStyle}>{doc.original ? "Yes" : "No"}</span>
            </>
          ) : null}
          {typeof doc.trafficable === "boolean" ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Trafficable</span>
              <span style={valueTextStyle}>{doc.trafficable ? "Yes" : "No"}</span>
            </>
          ) : null}
          {doc.validuntil ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Valid until</span>
              <span
                style={{
                  ...valueTextStyle,
                  color: documentExpiryColor(doc.validuntil) || undefined,
                  fontWeight: documentExpiryColor(doc.validuntil) ? 700 : undefined,
                }}
              >
                {formatDisplayDate(doc.validuntil)}
              </span>
            </>
          ) : null}
          {doc.status ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Status</span>
              <span style={valueTextStyle}>{documentStatusLabel(doc.status)}</span>
            </>
          ) : null}
          {doc.statusdate ? (
            <>
              <span style={{ width: 10 }} />
              <span style={labelPillStyle}>Status date</span>
              <span style={valueTextStyle}>{formatDisplayDate(doc.statusdate)}</span>
            </>
          ) : null}
        </div>
        {doc.logstatus ? (
          <div style={{ marginTop: 10 }}>
            <div className={"info_descr"}>Log status</div>
            <div className={"info_span"}>{doc.logstatus}</div>
          </div>
        ) : null}
        {doc.notes ? (
          <div style={{ marginTop: 10 }}>
            <div className={"info_descr"}>Notes</div>
            <div className={"info_span"}>
              <RichTextDisplay html={doc.notes} emptyLabel="" />
            </div>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );

  return (
    <>
      <div
        style={{
          margin: "0 20px 12px",
          padding: 12,
          background: "#fff",
          border: "1px solid #eee",
          borderRadius: 8,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          alignItems: "end",
        }}
      >
        <Form.Group style={{ marginBottom: 0 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: 12 }}>Search</Form.Label>
          <Form.Control
            type="text"
            placeholder="Title, ID, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Form.Group>
        <Form.Group style={{ marginBottom: 0 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: 12 }}>Category</Form.Label>
          <AutocompleteSelect
            value={categoryFilter}
            onChange={setCategoryFilter}
            placeholder="All categories"
            compact
          >
            <option value="__uncategorized__">Uncategorized</option>
            {categories.map((c) => (
              <option key={c.doccate_id} value={c.doccate_id}>
                {c.title}
              </option>
            ))}
          </AutocompleteSelect>
        </Form.Group>
        <Form.Group style={{ marginBottom: 0 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: 12 }}>Status</Form.Label>
          <AutocompleteSelect
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All statuses"
            compact
          >
            {DOCUMENT_STATUS_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </AutocompleteSelect>
        </Form.Group>
        <div style={{ fontSize: 13, color: "#666", paddingBottom: 8 }}>
          Showing {filtered.length} of {(documents || []).length}
        </div>
      </div>

      <div
        style={{
          margin: "0 20px 8px",
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "end",
        }}
      >
        <Form.Group style={{ marginBottom: 0, minWidth: 220 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: 12 }}>
            Upload into category
          </Form.Label>
          <AutocompleteSelect
            value={uploadCategoryId}
            onChange={setUploadCategoryId}
            placeholder="Uncategorized"
            compact
          >
            {categories.map((c) => (
              <option key={c.doccate_id} value={c.doccate_id}>
                {c.title}
              </option>
            ))}
          </AutocompleteSelect>
        </Form.Group>
      </div>

      <AddDocumentCategoryModal
        onDocumentCategoryCreated={(created) => {
          if (!created?.doccate_id) return;
          setCategories((prev) => {
            const next = [
              ...prev.filter((c) => c.doccate_id !== created.doccate_id),
              created,
            ].filter((c) => c.active !== false);
            next.sort(
              (a, b) =>
                (a.orderindex ?? 0) - (b.orderindex ?? 0) ||
                String(a.title).localeCompare(String(b.title))
            );
            return next;
          });
          setUploadCategoryId(created.doccate_id);
        }}
      />
      <DocumentDropzone
        projectId={projectId}
        clientId={clientId}
        categoryId={uploadCategoryId || null}
        refreshData={refreshData}
      />
      <AddDocumentModal
        refreshData={refreshData}
        defaultProjectId={projectId}
        defaultClientId={clientId}
        lockProject={lockProject}
        lockClient={lockClient}
        projectAssociatedClients={projectAssociatedClients}
        defaultCategoryId={uploadCategoryId || ""}
      />

      {grouped.length > 0 ? (
        grouped.map((group) => (
          <div key={group.id} style={{ margin: "0 20px" }}>
            <div style={sectionHeaderStyle} id={`doc-cat-${group.id}`}>
              <span>
                {group.title}{" "}
                <span style={{ fontWeight: 500, color: "#666" }}>
                  ({group.docs.length})
                </span>
              </span>
            </div>
            {group.docs.map(renderDocCard)}
          </div>
        ))
      ) : (
        <div style={{ margin: "16px 20px" }}>No documents match your filters.</div>
      )}
    </>
  );
}
